import type { InfiniteData, QueryClient } from '@tanstack/react-query';
import { produce } from 'immer';
import type { PodcastDTO } from '../dto/podcast.dto.js';
import type { PodcastEpisodeDTO } from '../dto/podcast-episode.dto.js';
import type { UserDTO } from '../dto/user.dto.js';
import type { PaginateResult } from '../models/paginate-result.js';
import type { User } from '../models/user.model.js';
import type { WithStringId } from '../utils/typescript.util.js';
import { userQueryKeys } from './query-keys.js';
import type { FuuzeQueryKey } from './shared.js';

export type PodcastInCache = WithStringId<PodcastDTO>;

export function cacheManager() {
  return {
    getPodcastEpisode,
    updatePodcastChannel,
    updatePodcastEpisode,
    updateUserOwnedPodcast,
    updateUser,
    getUser,
  };
}

function getPodcastEpisode(
  queryClient: QueryClient,
  matcher: (item: PodcastEpisodeDTO) => boolean,
): PodcastEpisodeDTO | undefined {
  const allCachedData = queryClient
    .getQueryCache()
    .findAll({
      predicate: query => (query.queryKey as FuuzeQueryKey)[1]?.type === 'podcastEpisode' && !!query.state.data,
    })
    .map(query => query.state.data as PodcastEpisodeDTO | InfiniteData<PaginateResult<PodcastEpisodeDTO>>);

  for (const data of allCachedData) {
    if ('pages' in data && 'pageParams' in data) {
      // Results are paginated
      for (const page of data.pages) {
        const episode = page.docs.find(matcher);
        if (episode) return episode;
      }
    } else {
      // Results are not paginated
      if (matcher(data)) return data;
    }
  }
}

/**
 * Will update the podcast in the cache if it's found.
 * Will also update the user's owned podcasts if the podcast is one of the user's owned podcasts.
 * @param queryClient
 * @param channelId
 * @param updates
 */
function updatePodcastChannel(queryClient: QueryClient, channelId: string, updates: Partial<PodcastInCache>) {
  queryClient
    .getQueryCache()
    .findAll({
      predicate: query =>
        (query.queryKey as FuuzeQueryKey)[1]?.type === 'podcastChannel' &&
        (query.state.data as PodcastInCache)._id === channelId,
    })
    .forEach(query => {
      query.setData(
        produce(query.state.data as PodcastDTO, (draft: PodcastInCache) => {
          Object.assign(draft, updates);
        }),
      );
    });

  // Update the podcast if it's one of the user's owned podcasts
  updateUserOwnedPodcast(queryClient, channelId, updates);
}

/**
 * Called also by the updatePodcastChannel function.
 */
function updateUserOwnedPodcast(queryClient: QueryClient, channelId: string, updates: Partial<PodcastInCache>) {
  const user = queryClient.getQueryData<UserDTO>(userQueryKeys.signIn);
  const userOwnedChannelMatchingIndex = user?.ownedChannels.findIndex(
    channel => typeof channel !== 'string' && channel._id === channelId,
  );
  if (userOwnedChannelMatchingIndex !== undefined) {
    queryClient.setQueryData(
      userQueryKeys.signIn,
      produce(user, draft => {
        if (!draft) return;
        draft.ownedChannels[userOwnedChannelMatchingIndex] = {
          ...(draft.ownedChannels[userOwnedChannelMatchingIndex] as PodcastInCache),
          ...updates,
        };
      }),
    );
  }
}

function updatePodcastEpisode(
  queryClient: QueryClient,
  filter: (episode: PodcastEpisodeDTO) => boolean,
  updates: (episode: PodcastEpisodeDTO) => void,
): void;
function updatePodcastEpisode(
  queryClient: QueryClient,
  filter: (episode: PodcastEpisodeDTO) => boolean,
  updates: Partial<PodcastEpisodeDTO>,
): void;
function updatePodcastEpisode(
  queryClient: QueryClient,
  filter: (episode: PodcastEpisodeDTO) => boolean,
  updates: ((episode: PodcastEpisodeDTO) => void) | Partial<PodcastEpisodeDTO>,
) {
  queryClient
    .getQueryCache()
    .findAll({
      predicate: query => (query.queryKey as FuuzeQueryKey)[1]?.type === 'podcastEpisode' && !!query.state.data,
    })
    .forEach(query => {
      const data = query.state.data as
        | PodcastEpisodeDTO
        | PodcastEpisodeDTO[]
        | InfiniteData<PaginateResult<PodcastEpisodeDTO>>;
      if ('pages' in data && 'pageParams' in data) {
        // Results are paginated
        query.setData(
          produce(data as InfiniteData<PaginateResult<PodcastEpisodeDTO>>, draft => {
            draft.pages.forEach(page => {
              page.docs.forEach(episode => {
                if (filter(episode)) {
                  if (typeof updates === 'function') updates(episode);
                  else Object.assign(episode, updates);
                }
              });
            });
          }),
        );
      } else if (Array.isArray(data)) {
        // List of results
        query.setData(
          produce(data, draft => {
            const index = draft.findIndex(e => filter(e));
            if (index !== -1) {
              if (typeof updates === 'function') updates(draft[index]!);
              else Object.assign(draft[index]!, updates);
            }
          }),
        );
      } else {
        // Results are not paginated
        query.setData(
          produce(data, draft => {
            if (filter(draft)) {
              if (typeof updates === 'function') updates(draft);
              else Object.assign(draft, updates);
            }
          }),
        );
      }
    });
}

function updateUser(queryClient: QueryClient, updates: (user: UserDTO) => void): void;
function updateUser(queryClient: QueryClient, updates: Partial<UserDTO>): void;
function updateUser(queryClient: QueryClient, updates: Partial<UserDTO> | ((user: UserDTO) => void)): void {
  queryClient.setQueryData(
    userQueryKeys.signIn,
    produce(getUser(queryClient), draft => {
      if (!draft) return;
      if (typeof updates === 'function') updates(draft);
      else Object.assign(draft, updates);
    }),
  );
}

function getUser(queryClient: QueryClient) {
  return queryClient.getQueryData<User>(userQueryKeys.signIn);
}
